import { css } from "@emotion/react"
import { useSignal, computed } from "@preact/signals"
import { memo, useRef, useCallback, useEffect } from "preact/compat"
import { AnimationModal, FixedBox, CenterBox } from "./styled"
import Container from "./Container"
import { createLogger } from "@/utils/logger"
import WebpAnimation, { type WebpAnimationHandle } from "@/components/composite/WebpAnimation/main"
const logger = createLogger("BlindBoxModal")

// 定义 Modal 组件的 Props 类型
type ModalProps = {
    full: boolean // 是否全屏显示
    dataSource: BlindBoxApiTypes.Fetcher.Response.Record[] // 盲盒数据源
    onCancel: () => void // 取消操作的回调函数
    handleClaim: (boxId: string) => Promise<void> // 处理盲盒领取的回调函数
    onClaimAll: () => void // 动画结束的回调函数
    onTimeEnd: () => void // 倒计时结束的回调函数
}

const Modal: React.FC<ModalProps> = ({
    full,
    dataSource,
    onCancel,
    handleClaim,
    onClaimAll,
    onTimeEnd,
}) => {
    const animationRef = useRef<WebpAnimationHandle>(null) // WebpAnimation 的引用
    const boxNum = useSignal(dataSource?.length ?? 0) // 盲盒数量状态
    const currentBox = computed(() => dataSource?.[0] ?? {}) // 当前盲盒数据（计算属性）
    const boxEndAnimWhile = useRef(true)
    useEffect(() => {
        return () => {
            boxEndAnimWhile.current = false
        }
    }, [])

    // 设置盲盒动画初始状态
    const setBox = useCallback(async () => {
        animationRef.current?.set(0)
        await animationRef.current?.move(40)
    }, [])

    const boxEndAnim = useCallback(async () => {
        await animationRef.current?.move(90)
        await animationRef.current?.moveEnd()
        setTimeout(async () => {
            while (boxEndAnimWhile.current) {
                await animationRef.current?.move(90)
                await animationRef.current?.moveEnd()
            }
        }, 0)
    }, [])

    const claimSuccess = useSignal(false)
    const loading = useSignal(false)
    // 处理打开盲盒的逻辑
    const handleOpen = useCallback(async () => {
        if (loading.value) return
        const id = currentBox.value.box_id
        if (!id) return

        try {
            loading.value = true
            await handleClaim(id) // 调用领取逻辑
            await boxEndAnim()
            claimSuccess.value = true
            setTimeout(() => {
                onClaimAll()
            }, 3000)
        } catch (error) {
            logger.debug("handleOpen", error)
        } finally {
            loading.value = false
        }
    }, [currentBox.value.box_id, loading, handleClaim, boxEndAnim, claimSuccess, onClaimAll])

    useEffect(() => {
        if (!full) {
            return
        }
        const timeout = setTimeout(async () => {
            if (full && boxNum.value > 0) {
                await setBox()
            }
        }, 500)

        return () => {
            clearTimeout(timeout)
        }
    }, [boxNum, setBox, full])

    return (
        <FixedBox
            animate={{
                display: full ? "flex" : "none",
            }}
            transition={{ duration: 0.3, ease: "backIn" }}
        >
            <CenterBox
                onClick={() => {
                    if (!claimSuccess.value) return
                    onClaimAll()
                }}
            >
                <AnimationModal
                    animate={{
                        top: full ? "0" : "auto",
                        bottom: full ? "auto" : "56rem",
                        left: full ? "0" : "auto",
                        right: full ? "auto" : "0",
                        backgroundColor: full ? "rgba(32, 32, 32, 0.9)" : "rgba(32, 32, 32, 0)",
                        width: full ? "100%" : "60rem",
                        height: full ? "100%" : "60rem",
                        opacity: full ? 1 : 0,
                    }}
                    initial={{
                        top: "0",
                        bottom: "0",
                        left: "0",
                        right: "0",
                    }}
                    transition={{ duration: 0.5, ease: "easeOut" }}
                >
                    {/* 全屏模式下显示 Container 组件 */}
                    {full && (
                        <Container
                            handleOpen={handleOpen}
                            handleCancel={onCancel}
                            timer={currentBox.value.expire_second}
                            onTimeEnd={onTimeEnd}
                            claimSuccess={claimSuccess.value}
                            amount={currentBox.value.bonus}
                            loading={loading.value}
                        />
                    )}
                    {full ? (
                        <WebpAnimation
                            ref={animationRef}
                            animationKey="mystery_box"
                            frameDuration={50}
                            css={css`
                                z-index: 10;
                                align-self: stretch;
                                padding: 0 25rem;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                            `}
                        />
                    ) : null}
                </AnimationModal>
            </CenterBox>
        </FixedBox>
    )
}

export default memo(Modal)
